import { getMissionStatus, getObjectId, isMissionComplete, makeHash, getRefId } from "../utils/utils"
import { getAllActionItems, getMissionPercentComplete } from "../comp/MissionUtils"
import { isMissionBillable, missionFinancials } from "../utils/financials"

import { getOrgPlanItems } from "../services/api/v2/planItem"

import { getOrgActionItemsProgressLite } from "../services/api/v2/actionItem"
import {
    differenceInWeeks,
    differenceInDays,
    startOfDay,
    isAfter,
    isBefore,
    endOfDay,
    isSameDay,
    differenceInHours,
    subSeconds,
    differenceInMinutes,
} from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { getTimeUtc } from "../utils/dates"
import groupBy from "lodash/groupBy"
import { getProfile } from "../services/auth/auth.api"
import { getOrgPeople } from "../services/api/v2/orgPerson"
import apiActionItem from "../services/actionItem/actionItem.api"
import { v4 as uniqid } from "uuid"
import { MISSION_PERMISSION } from "../services/mission/mission.const"
import update from "immutability-helper"
import { buildMissionChange } from "./missionChange"
import pLimit from "p-limit"
import chunk from "lodash/chunk"
import uniqBy from "lodash/uniqBy"
import { paginateAll, paginateChunked } from "../services/api/utils"
import { getMissions } from "../services/api/v2/mission"
import { getDbItem, addDbItem } from "./idb-util"
import { getOrgApprovedTimesheetsUltraLean } from "../services/api/v2/timesheet"

import { ensureNumber } from "./mf-utils"
import { getMonthlyInLine } from "./monthlyAmounts"
import { getTimesheetHours } from "../comp/DateUtils"

// 1. Map to store in-flight requests
const pendingRequests = new Map()

const MISSION_BASIC_SELECT = [
    "_id",
    "projectCode",
    "title",
    "projectStatus",
    "noTimesheets",
    "_archived",
    "client",
    "kickedOff",
    "theme.backgroundImage",
    "budgetAvailable",
    "responsibleDepartment",
    "currency",
    "isProposal",
    "isProcess",
]

// Same 10 minute rule as getMissionBasicDetails: a record cached with other extra fields is a miss.
const isFreshMissionBasicDetails = (dm, extraParams) =>
    Boolean(dm?.mission) &&
    differenceInMinutes(new Date(), dm.updatedAt) <= 10 &&
    !(extraParams?.length && extraParams.join(",") !== dm.extraParamsString)

/**
 * Batch version of getMissionBasicDetails for lists (one row per project). Reads the
 * same cache, fetches only the misses in chunks of ids instead of one request per
 * project, and writes them back so the single version and other screens hit them.
 *
 * @returns {Promise<Object<string, Object>>} mission by id; ids the API did not return are absent
 */
export const getMissionsBasicDetails = async ({ missionIds, extraParams, concurrency = 1, signal }) => {
    const ids = [...new Set((missionIds || []).map(getObjectId).filter(Boolean))]
    const byId = {}

    const cached = await Promise.all(
        ids.map((id) => getDbItem("missionBasicDetails", "store", id).catch(() => undefined)),
    )

    const missing = []
    ids.forEach((id, i) => {
        if (isFreshMissionBasicDetails(cached[i], extraParams)) {
            byId[id] = cached[i].mission
        } else {
            missing.push(id)
        }
    })

    if (!missing.length) {
        return byId
    }

    const fetched = await paginateChunked({
        fn: (params) => paginateAll(getMissions(params)),
        params: {
            id: missing,
            limit: 100,
            withArchived: true,
            select: [...MISSION_BASIC_SELECT, ...(extraParams || [])],
        },
        chunkKey: "id",
        chunkSize: 50,
        concurrency,
        signal,
    })

    const updatedAt = new Date()
    const extraParamsString = extraParams?.join(",") || null

    await Promise.all(
        (fetched || []).map((missionData) => {
            byId[missionData._id] = missionData

            return addDbItem("missionBasicDetails", "store", {
                id: missionData._id,
                mission: missionData,
                updatedAt,
                extraParamsString,
            })
        }),
    )

    return byId
}

export const getMissionBasicDetails = async ({ missionId, extraParams, forceRefresh }) => {
    // 2. Create a unique key for this specific request
    const cacheKey = `${missionId}-${extraParams?.join(",") || "no-extras"}`

    // 3. If a request is already running, return its promise
    if (pendingRequests.has(cacheKey) && !forceRefresh) {
        return pendingRequests.get(cacheKey)
    }

    const fetchPromise = (async () => {
        try {
            const dm = await getDbItem("missionBasicDetails", "store", missionId)

            if (!forceRefresh && dm?.mission && differenceInMinutes(new Date(), dm.updatedAt) <= 10) {
                if (extraParams?.length && extraParams.join(",") !== dm.extraParamsString) {
                    // do nothing. continue
                } else {
                    return dm.mission
                }
            }

            const gm = await getMissions({
                id: missionId,
                withArchived: true,
                select: [...MISSION_BASIC_SELECT, ...(extraParams || [])],
            })

            const missionData = gm?.value?.[0]

            if (missionData) {
                await addDbItem("missionBasicDetails", "store", {
                    id: missionId,
                    mission: missionData,
                    updatedAt: new Date(),
                    extraParamsString: extraParams?.join(",") || null,
                })
            }

            return missionData
            // --- YOUR EXACT ORIGINAL LOGIC ENDS HERE ---
        } finally {
            // 5. Clean up the Map when the promise resolves or rejects
            pendingRequests.delete(cacheKey)
        }
    })()

    // 6. Store the promise in the map before returning it
    pendingRequests.set(cacheKey, fetchPromise)

    return fetchPromise
}

export const getAsynMissionPercentComplete = async (orgId, missionId) => {
    const actionItems = await paginateAll(
        getOrgActionItemsProgressLite(orgId, {
            project: missionId,
        }),
    )

    const gm = await getMissions({
        id: missionId,
        select: ["projectType"],
        org: orgId,
    })

    const mission = gm.value[0]

    let planItems = []

    if (!mission) {
        return 0
    }

    if (mission?.projectType === "mx-gantt") {
        planItems = await paginateAll(
            getOrgPlanItems(orgId, {
                project: missionId,
                type: "person",
                select: ["raciOnTasks", "type"],
                org: orgId,
            }),
        )
    }

    const c = getMissionPercentComplete(mission, false, actionItems, planItems)

    return c
}

export function getThumb(mission) {
    return (
        mission?.theme?.backgroundImage?.replace(".jpg", "-thumb.jpg") ||
        "backgroundImage:url(/themes/missions/bg-26-thumb.jpg)"
    )
}

export function getTile(mission) {
    return (
        mission?.theme?.backgroundImage?.replace(".jpg", "-tile.jpg") ||
        "backgroundImage:url(/themes/missions/bg-26-tile.jpg)"
    )
}

export const mergeMissionData = ({ missions, planItems, actions = [], actionItems }) => {
    const planItemsByMission = groupBy(planItems, "missionId")
    const actionsByMission = groupBy(actions, "missionId")
    const actionsByPlanItem = groupBy(actions, "missionPlanId")
    const actionItemsByAction = groupBy(actionItems, "actionId")

    return missions.map((mission) => {
        const pis = planItemsByMission[mission._id] || []

        let mapppedPis

        if (!pis.find((p) => p.type === "phase")) {
            mapppedPis = [
                ...pis,
                {
                    type: "phase",
                    startDate: mission.planStartDate,
                    endDate: mission.planEndDate,
                    actions: (actionsByMission[mission._id] || [])?.map((ac) => {
                        return {
                            ...ac,
                            actionItems: actionItemsByAction[ac._id] || [],
                        }
                    }),
                },
            ]
        } else {
            mapppedPis = pis.map((pi) => {
                const myActions = (actionsByPlanItem[pi._id] || []).map((ac) => ({
                    ...ac,
                    actionItems: actionItemsByAction[ac._id] || [],
                }))

                return {
                    ...pi,
                    actions: myActions || [],
                }
            })
        }

        return {
            ...mission,
            planItems: mapppedPis,
        }
    })
}

export const duplicateCostPlanItem = async ({ pi, mission, app }) => {
    // Create a new object excluding the fields we don’t want
    const { _id, createdAt, createdBy, updatedAt, ...newPlanItem } = pi

    // Create the duplicated plan item - with the typed months its dates cover, not ones kept
    // from dates it no longer has
    return app.planItemCreateX(mission._id, {
        ...newPlanItem,
        ...(pi.monthly ? { monthly: getMonthlyInLine(pi) } : {}),
    })
}
export const duplicateRaciRoles = ({ oldAi, newAi, mission, app }) => {
    // Create a new object excluding the fields we don’t want
    const rolesToMerge = (mission.planItems || []).filter((p) => p.raciOnTasks?.find((rt) => rt.taskId === oldAi._id))

    rolesToMerge.forEach((pi) => {
        const myRacis = pi.raciOnTasks?.slice()
        const myRaci = myRacis.find((r) => r.taskId === oldAi._id)
        if (!myRaci) return
        const { _id, createdAt, createdBy, updatedAt, id, ...rest } = myRaci
        const dupeRaciForNewTask = { ...rest, taskId: newAi._id, id: uniqid(), actualHours: null }

        myRacis.push(dupeRaciForNewTask)

        app.planItemUpdateX(pi, {
            raciOnTasks: myRacis,
        })
    })
}
export const completeMission = (mission, app) => {
    app.missionUpdate(mission._id, {
        projectStatus: "completed",
        missionStop: getTimeUtc(new Date()),
    })
}
export const openMission = (mission, app) => {
    app.missionUpdate(mission._id, {
        projectStatus: "active",
        missionStop: null,
    })
}

export const IMPORTANCE_ICONS = [
    { value: 100, icon: "💥" },
    { value: 75, icon: "🔥" },
    { value: 50, icon: "⚡" },
    { value: 25, icon: "🍃" },
]

export const IMPORTANCE_VALUES = [
    { title: "💥 Mission critical", value: 100, icon: "💥" },
    { title: "🔥 High importance", value: 75, icon: "🔥" },
    { title: "⚡ Normal priority", value: 50, icon: "⚡" },
    { title: "🍃 Low priority", value: 25, icon: "🍃" },
]

/**
 * Returns an emoji icon based on importance value.
 * Falls back to ⚡ for unknown values.
 */
export const getImportanceIcon = (value) => {
    return IMPORTANCE_ICONS.find((item) => item.value === value)?.icon ?? "⚡"
}

export function getWBS(packages, nodeId) {
    const packageMap = new Map(packages.map((pkg) => [pkg._id, pkg]))

    if (!packageMap.has(nodeId)) {
        return ""
    }

    let wbs = []
    let current = packageMap.get(nodeId)

    while (current) {
        const parent = packageMap.get(current.parentId)

        if (!parent) {
            wbs.unshift(`${current.position + 1}`)
        } else {
            // Get all siblings, properly sorted
            const siblings = packages
                .filter((pkg) => pkg.parentId === current.parentId)
                .sort((a, b) => {
                    if (a.isAction !== b.isAction) {
                        return a.isAction ? 1 : -1 // Ensure packages before actions
                    }
                    return a.sortIndex - b.sortIndex
                })

            // Find the current node's position among its siblings
            const index = siblings.findIndex((pkg) => pkg._id === current._id)
            wbs.unshift(`${index + 1}`)
        }

        current = parent
    }

    return wbs.join(".")
}

export function getSortedPackages(packages) {
    // Step 1: Build a lookup map
    const packageMap = new Map(packages.map((pkg) => [pkg._id, { ...pkg, children: [] }]))

    // Step 2: Organize packages into a tree
    let rootPackages = []
    for (const pkg of packages) {
        const node = packageMap.get(pkg._id)
        if (!pkg.parentId) {
            rootPackages.push(node)
        } else {
            const parent = packageMap.get(pkg.parentId)
            if (parent) {
                parent.children.push(node)
            }
        }
    }

    // Step 3: Sort each level by isAction (activities first) then original sortIndex

    function sortTree(nodes) {
        nodes.sort((a, b) => {
            const aIsAction = !!a.isAction
            const bIsAction = !!b.isAction
            if (aIsAction !== bIsAction) {
                return aIsAction - bIsAction // Activities before packages
            }
            // Sort by position, then createdAt
            return (a.position ?? Infinity) - (b.position ?? Infinity)
        })
        nodes.forEach((node) => sortTree(node.children))
    }

    sortTree(rootPackages)

    // Step 4: Flatten tree into a sorted list
    let sortedPackages = []
    function flattenTree(nodes) {
        for (const node of nodes) {
            const { children, ...pkg } = node
            sortedPackages.push(pkg)
            flattenTree(children)
        }
    }
    flattenTree(rootPackages)

    // Step 5: Reassign sortIndex sequentially based on flattened order
    const sP = sortedPackages.map((pkg, index) => {
        return {
            ...pkg,
            sortIndexDate: index + 1, // Sequential sortIndex
        }
    })

    // Step 6: Assign WBS using the sorted sP array
    return sP.map((pkg) => ({
        ...pkg,
        wbs: getWBS(sP, pkg._id),
    }))
}

/**
 * @param {Object} [params.financialsByMission] Whole-project totals per mission id in the project's
 *   own currency (stored aggregates). When given, budgets are checked against these and the
 *   missions only need their role plan items; otherwise every mission is costed in the browser.
 */
export function missionWarnings({ missions, app, orgData, timesheets = [], financialsByMission }) {
    const data = []
    const timesheetsByMission = groupBy(
        timesheets,
        (ts) => getObjectId(ts.missionId) || getObjectId(ts.mission) || getObjectId(ts.snapshot?.mission),
    )

    missions.forEach((mission) => {
        const status = getMissionStatus(mission)
        if (status !== "active" || isMissionComplete(mission)) return

        const isBillable = isMissionBillable(mission)
        const org = orgData || app.state.orgs?.find((o) => getObjectId(o) === getObjectId(mission.org))
        const mf = financialsByMission
            ? { estCost: 0, estRevenue: 0, actualCost: 0, ...financialsByMission[mission._id] }
            : missionFinancials({
                  mission,
                  app,
                  orgData: org,
                  timesheets: timesheetsByMission[mission._id] || [],
              })

        if (
            isBillable &&
            mission.budgetAvailable &&
            (mission.budgetAvailable < mf.estCost || mission.budgetAvailable < mf.actualCost)
        ) {
            data.push(
                createWarning("over-budget", 1, mission, {
                    budgetAvailable: mission.budgetAvailable,
                    estCost: mf.estCost,
                    actualCost: mf.actualCost,
                    startDate: new Date(),
                }),
            )
        } else if (isBillable && mission.budgetAvailable && mission.budgetAvailable !== mf.estRevenue) {
            data.push(
                createWarning("undefined-budget", 2, mission, {
                    budgetAvailable: mission.budgetAvailable,
                    definedBudget: mf.estRevenue,
                    // A state of now, like over budget: the dot sits at today
                    startDate: new Date(),
                }),
            )
        }

        mission.planItems.forEach((pi) => {
            if (pi.type === "person" && !pi.person) {
                const startDate = toZonedTime(pi.startDate)
                const today = startOfDay(new Date())
                const daysTo = differenceInDays(startDate, today)
                const weeksSinceStart = differenceInWeeks(today, startDate)

                // Case 1: Future item starting within 10 days
                if (isAfter(startDate, today) && daysTo <= 10) {
                    data.push(createWarning("unfilled-role", daysTo > 5 ? 2 : 3, mission, { pi, startDate }))
                }
                // Case 2: Past or current item (started 0 or more weeks ago)
                else if (weeksSinceStart >= 0) {
                    // High priority (3) for current or recent (<= 2 weeks), lower or same priority for older items
                    const priority = weeksSinceStart <= 2 ? 3 : 2
                    // The dot sits at today while the role runs, on its end date once it ended
                    // (the tip always shows the role's own start)
                    const endDate = pi.endDate ? toZonedTime(pi.endDate) : null
                    const effectiveDate = endDate && isBefore(endDate, today) ? endDate : today

                    data.push(createWarning("unfilled-role", priority, mission, { pi, startDate: effectiveDate }))
                }
            }
        })
    })

    return data
}

function createWarning(type, severity, mission, additionalFields = {}) {
    return {
        date: (additionalFields.startDate || toZonedTime(mission.planStartDate)).getTime() + "",
        type,
        severity,
        mission,
        missionId: mission._id,
        ...additionalFields,
    }
}

/**
 * Given a list of missions, it finds which org members should be attached to them as org observers and returns the list of missions with org observers attached.
 * This is part of the removal of org observers, to make the UI backwards compatible until refactored.
 * TODO: @orgObservers Remove when org observers are refactored
 */
export async function getMissionListWithOrgObservers(missionList) {
    if (!missionList?.length) {
        return []
    }

    const profile = await getProfile()
    const notOnMissionList = missionList.filter(
        (mission) =>
            !mission.people?.find((person) => {
                return getObjectId(person.ref) === profile._id
            }),
    )

    if (notOnMissionList?.length) {
        const orgIdList = Array.from(
            new Set(notOnMissionList.filter((mission) => !!mission.org).map((mission) => getObjectId(mission.org))),
        )

        if (orgIdList?.length) {
            const orgListResult = await Promise.allSettled(
                orgIdList.map((orgId) =>
                    getOrgPeople(orgId, {
                        person: profile._id,
                        permission: ["admin", "manager"],
                    }),
                ),
            )

            const orgPersonList = orgListResult
                .filter((result) => result.status === "fulfilled")
                .map((result) => result.value?.value?.[0])
            const orgIdToOrgPersonMap = new Map(orgPersonList.map((person) => [getObjectId(person.org), person]))

            return [...missionList].map((mission) => {
                const missionPersonList = mission.people ?? []

                if (mission.org) {
                    const missionPerson = mission.people?.find((person) => getObjectId(person.ref) === profile._id)
                    const orgPerson = orgIdToOrgPersonMap.get(getObjectId(mission.org))

                    if (
                        !missionPerson &&
                        ((orgPerson?.permission === 1 && !orgPerson?.isBasicCompanyAdmin) ||
                            orgPerson?.permission === 2)
                    ) {
                        missionPersonList.push({
                            _id: orgPerson._id,
                            ref: orgPerson.ref,
                            email: orgPerson.email,
                            permission: MISSION_PERMISSION.OBSERVER_ORG,
                        })
                    }
                }

                return {
                    ...mission,
                    people: missionPersonList,
                }
            })
        }
    } else {
        return missionList
    }
}

export const replaceRaciTasks = async ({ app, mission, planItem, personToReplace }) => {
    const allAi = getAllActionItems(mission)
    const aiHash = makeHash(allAi)

    app.block("One moment please updating tasks")

    //delete

    const apiDeleteCalls = (planItem.raciOnTasks || [])
        .map((rt, i) => {
            const myAi = aiHash[rt.taskId]

            // Check if action item exists and person is not already attached
            if (myAi && myAi.people.includes(getRefId(personToReplace)) && myAi.status !== "done") {
                return apiActionItem.deleteActionItemPeople(myAi._id, getRefId(personToReplace)).then((res) => ({
                    res,
                    alt: {
                        people: res.people,
                        peopleAttached: res.peopleAttached,
                    },
                    index: i,
                }))
            }
            return null
        })
        .filter(Boolean) // Remove null values

    // Collect all API calls into an array of promises

    // Execute all API calls concurrently
    const results = await Promise.all(apiDeleteCalls)

    // Update state for all successful responses
    results.forEach(({ res, alt }) => {
        app.updateStateForActionItemUpdate(res, alt, false)
    })

    app.unBlock()
}

export const getMissionApprovedHoursFromTimesheets = async (missionId, orgId) => {
    const db = await getDbItem("timesheetsByMissionApprovedHours", "store", missionId)

    let timesheets = []

    if (db?.timesheets?.length && differenceInHours(new Date(), db?.updatedAt) < 24) {
        return db.hours
    }

    if (db?.timesheets) {
        timesheets.push(...db.timesheets)
    }

    const gs = await paginateAll(
        getOrgApprovedTimesheetsUltraLean(orgId, {
            project: missionId,
            updatedAt: db?.updatedAt ? `gte ${db?.updatedAt.toISOString()}` : undefined,
        }),
    )

    timesheets.push(...(gs || []))

    timesheets = uniqBy(timesheets.reverse(), "_id")

    let hours = 0
    timesheets.forEach((ts) => {
        hours += ensureNumber(getTimesheetHours(ts))
    })

    if (timesheets.length) {
        await addDbItem("timesheetsByMissionApprovedHours", "store", {
            id: missionId,
            timesheets: timesheets,
            hours: hours,
            updatedAt: subSeconds(new Date(), 5),
        })
    }
    return hours
}

export const fillRaciTasks = async ({ app, mission, planItem, newPerson }) => {
    const allAi = getAllActionItems(mission)
    const aiHash = makeHash(allAi)

    app.block("One moment please assigning people to tasks")

    // One task at a time: every assignment appends its task to the person's single
    // "tasks assigned" email for the project, and parallel calls race on that write.
    const settled = []

    for (const rt of planItem.raciOnTasks || []) {
        if (mission.isTemplate) break

        const myAi = aiHash[rt.taskId]
        const personRefId = newPerson ? getRefId(newPerson) : planItem.person
        const myOrg = app?.state?.orgs.find((o) => o._id === getObjectId(mission))

        if (!myAi || myAi.people.includes(personRefId) || myAi.status === "done") continue

        try {
            const value = await apiActionItem.addActionItemPeople({
                actionItemId: myAi._id,
                peopleId: personRefId,
                options: {
                    notify: !myOrg?.disableSetupNotifications,
                },
            })

            settled.push({ status: "fulfilled", value })
        } catch (reason) {
            settled.push({ status: "rejected", reason })
        }
    }

    settled
        .filter((r) => r.status === "fulfilled" && r.value)
        .forEach(({ value: res }) => {
            app.updateStateForActionItemUpdate(
                res,
                {
                    people: res.people,
                    peopleAttached: res.peopleAttached,
                },
                false,
            )
        })

    app.unBlock()
}
export const PROCESS_SECTIONS = [
    {
        id: "intro",
        name: "Reason for the project",
        value: "",
        instructions: "Why are we doing this? Define the problem,  opportunity or business need",
    },
    {
        id: "objective",
        name: "Project objectives",
        value: "",
        instructions: "What do we hope to achieve?",
    },
    {
        id: "case",
        name: "Business case",
        value: "",
        instructions: "Describe the expected benefit to key stakeholders",
    },
    {
        id: "goals",
        name: "Goals/Metrics",
        value: "",
        instructions: "What does success look like and how will we measure this?",
    },
    {
        id: "scopeIn",
        name: "High-level scope",
        value: "",
        instructions: "Briefly summarize what is in scope for this project",
    },
    {
        id: "scopeOut",
        name: "Not in scope",
        value: "",
        instructions: "Note some key items that will not be part of this project's scope",
    },
    {
        id: "risks",
        name: "Risks",
        value: "",
        instructions: "Note some key items that will not be part of this project's scope",
    },
    {
        id: "contraints",
        name: "Constraints",
        value: "",
        instructions: "Note some key items that will not be part of this project's scope",
    },
    {
        id: "assumptions",
        name: "Assumptions",
        value: "",
        instructions: "Note some key items that will not be part of this project's scope",
    },
    {
        id: "brd",
        name: "High-level requirements",
        value: "",
        instructions: "Capture the high-level needs that will be used to create your WBS",
    },
]

export const getChunckedMissions = async (missionIds, chunkSize, params) => {
    if (!missionIds || missionIds?.filter(Boolean).length === 0) {
        return []
    }

    let accumulatedResults = []
    const chunked = chunk(missionIds, chunkSize)

    // Adjust concurrency based on total number of IDs
    const concurrency = missionIds.length < 200 ? 20 : 6
    const limit = pLimit(concurrency)

    // Map over chunks and process them with limited concurrency
    const promises = chunked.map((chunkIds) => {
        return limit(async () => {
            // Assuming getMyMissions handles pagination internally and returns all results
            const results = await paginateAll(
                getMissions({
                    id: chunkIds, // note: chunkIds, not [chunk]
                    ...params,
                }),
            )

            return results
        })
    })

    // Wait for all chunks to complete
    const chunkResults = await Promise.all(promises)

    // Flatten all results
    accumulatedResults = chunkResults?.flat().filter(Boolean) || []

    return accumulatedResults
}

export const getProposalStatus = (proposal, checkDates) => {
    const noApprovalStatus = proposal.proposalApproved !== false && proposal.proposalApproved !== true

    const stat =
        proposal.proposalStatus === "dropped"
            ? "dropped"
            : proposal.proposalStatus === "cancelled"
              ? "cancelled"
              : proposal.proposalStatus === "Submitted" && noApprovalStatus
                ? "submitted"
                : proposal.proposalApproved
                  ? "won"
                  : proposal.proposalApproved === false
                    ? "lost"
                    : "active"

    if (checkDates && stat === "active" && proposal.missionStop) {
        if (isBefore(toZonedTime(proposal.missionStop), new Date())) {
            return "past due"
        } else {
            return "active"
        }
    }

    return stat
}
export const reconcileCostItems = ({ mission, app, planItemToUse }) => {
    const missionCosts = planItemToUse
        ? [planItemToUse]
        : mission.planItems.filter((p) => p.type === "cost" && p.linkedToTask)

    const aiHash = makeHash(getAllActionItems(mission), "_id")
    let changes = []

    missionCosts.forEach((pi) => {
        const ai = aiHash[pi.linkedToTask]
        if (!ai) return

        const aiStart = toZonedTime(ai.startDate)
        const aiEnd = toZonedTime(ai.endDate)
        let newStart = toZonedTime(pi.startDate)
        let newEnd = toZonedTime(pi.endDate)

        // SCENARIO 1: Pinned to Task (Full Duration)
        if (pi.pinnedTo === "task") {
            newStart = aiStart
            newEnd = aiEnd
        }
        // SCENARIO 2: One-time (Glued to the Finish Line)
        else if (pi.rateTime === "One time") {
            const isOutOfBounds = newEnd < aiStart || newStart > aiEnd

            if (isOutOfBounds) {
                // If it fell outside, we snap it to the very end of the task
                newStart = startOfDay(aiEnd)
                newEnd = aiEnd
            }
        }
        // SCENARIO 3: Monthly / Not Pinned (Clamping/Proportional)
        else {
            // Keep it within bounds: Push start forward if AI starts later
            if (aiStart > newStart) newStart = aiStart
            // Pull end back if AI ends earlier
            if (aiEnd < newEnd) newEnd = aiEnd

            // Safety check: don't let start exceed end
            if (newStart > newEnd) newStart = startOfDay(newEnd)
        }

        // Only update if something actually changed
        if (!isSameDay(newStart, toZonedTime(pi.startDate)) || !isSameDay(newEnd, toZonedTime(pi.endDate))) {
            changes.push({
                obj: pi,
                change: {
                    _id: pi._id,
                    startDate: getTimeUtc(startOfDay(newStart)),
                    endDate: getTimeUtc(endOfDay(newEnd)),
                },
            })
        }
    })

    if (changes.length) {
        app.planItemUpdateBulk(mission._id, changes)
    }
}

export const getMissionTitle = (mission) => {
    if (!mission) {
        return "Mission not found"
    }
    return mission?.projectCode ? mission.projectCode + "-" + mission.title : mission.title
}

/**
 * Merges a change into one of app.state.missions (or adds it, with push) and then publishes
 * app.state.missionChange, which open project screens follow. cb gets the updated mission.
 * The App's missionUpdateState; here so its state behaviour can be tested on its own.
 *
 * @param app {object} The App: state, commitState and silentFail
 * @param id {string} Mission id
 * @param change {object} Fields to merge (the whole mission when push adds it)
 * @param [cb] {function} Called with the updated mission once both commits are done
 * @param [push] {boolean} Add the mission when it isn't in state
 */
export function missionUpdateState(app, id, change, cb, push) {
    let changedIndex = app.state.missions.findIndex((mission) => mission._id === id)

    let changeObj

    try {
        if (changedIndex !== -1) {
            changeObj = {
                missions: update(app.state.missions, {
                    [changedIndex]: { $merge: change },
                }),
            }
        } else if (push) {
            let newMs = app.state.missions.slice()
            newMs.push(change)
            changedIndex = newMs.length - 1
            changeObj = {
                missions: newMs,
            }
        }
    } catch (e) {
        app.silentFail("emknrwje3oijrmkew", e?.message || e)
    }

    if (changedIndex === -1) {
        return
    }

    app.commitState(changeObj, () => {
        app.commitState(
            {
                missionChange: buildMissionChange([
                    {
                        for: "mission",
                        change: change,
                        obj: app.state.missions[changedIndex],
                        missionId: app.state.missions[changedIndex]._id,
                    },
                ]),
            },
            () => {
                if (cb) {
                    cb(app.state.missions[changedIndex])
                }
            }
        )
    })
}
